import React, { forwardRef } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { formatNumberWithCommas } from 'common/utils/common.util';
import SimpleCard from '../general/SimpleCard';
import { ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { Calculation } from './construction-cost-report.type';
import { GroupedBarChartData } from '../recharts/CCReportGroupedViewBarChart';
import CCReportIndividualGraphData, { GraphData } from '../recharts/CCReportSingleViewBarChart';
import DetailedChartViewDialog from '../recharts/DetailedChartViewDialog';
import EmptyChart from '../recharts/EmptyChart';
import { CHART_SELECTOR_HEIGHT } from './construction-cost-report.constants';
import CCReportScatterPlot, { ScatterPlotData } from '../recharts/CCReportScatterPlot';
import { ReportTypes } from '../../common/utils/util.type';

interface CostCardProps {
  report?: any;
  heading: string;
  grossMultiplier: number;
  displayProjections: boolean;
  minValue: number;
  maxValue: number;
  avgValue: number;
  graphData?: {
    avgByLocation: GraphData[];
    avgByType: GraphData[];
  };
  groupedData?: GroupedBarChartData[];
  scatterPlotData?: ScatterPlotData[];
  typeOfReport: ReportTypes;
}

const CCReportCostCard = forwardRef<HTMLDivElement, CostCardProps>(
  (
    {
      report,
      heading,
      grossMultiplier,
      displayProjections,
      maxValue,
      avgValue,
      minValue,
      graphData,
      groupedData,
      scatterPlotData,
      typeOfReport,
    },
    ref,
  ) => {
    const [openChartDialog, setOpenChartDialog] = React.useState(false);
    const handleDialogOpen = () => {
      setOpenChartDialog(true);
    };

    const handleDialogClose = () => {
      setOpenChartDialog(false);
    };

    const renderData = report && graphData && (graphData?.avgByLocation?.length > 0 || graphData?.avgByType?.length > 0);
    const notEnoughData = report && graphData && graphData?.avgByLocation?.length <= 1 && graphData?.avgByType?.length <= 1;
    const renderScatterPlotData = Boolean(report && scatterPlotData && scatterPlotData.length > 0);

    const shouldRenderFallback =
      (typeOfReport === ReportTypes.QCC && renderData === false) || (typeOfReport === ReportTypes.CC && renderScatterPlotData === false);

    return (
      <>
        <SimpleCard alignItems='center'>
          {renderData ? (
            <>
              <Typography variant='h6' color='primary' sx={{ textTransform: 'uppercase', marginTop: '8px' }}>
                {heading}
              </Typography>
              {notEnoughData ? (
                <EmptyChart height={400} marginTop={CHART_SELECTOR_HEIGHT} />
              ) : (
                <CCReportIndividualGraphData
                  dataByLocation={graphData.avgByLocation}
                  dataByType={graphData.avgByType}
                  openDetailedView={handleDialogOpen}
                />
              )}
              <Divider sx={{ marginY: '16px', borderColor: 'rgba(0, 0, 0, 0.12)', width: '100%' }} />
            </>
          ) : null}{' '}
          {renderScatterPlotData && scatterPlotData ? (
            <>
              <Typography variant='h6' color='primary' sx={{ textTransform: 'uppercase', marginTop: '8px' }}>
                {heading}
              </Typography>
              {/* Attach ref directly to CCReportScatterPlot */}
              <CCReportScatterPlot ref={ref} data={scatterPlotData} />
              <Divider sx={{ marginY: '16px', borderColor: 'rgba(0, 0, 0, 0.12)', width: '100%' }} />
            </>
          ) : null}
          {!displayProjections &&
            renderCalculations(
              [
                {
                  budget: maxValue,
                  aggregation: 'High',
                },
                {
                  budget: avgValue,
                  aggregation: 'Average',
                },
                {
                  budget: minValue,
                  aggregation: 'Low',
                },
              ],
              false,
              heading,
              shouldRenderFallback,
            )}
          {displayProjections &&
            renderCalculations(
              [
                {
                  budget: maxValue * (grossMultiplier || 0),
                  aggregation: 'High',
                },
                {
                  budget: avgValue * (grossMultiplier || 0),
                  aggregation: 'Average',
                },
                {
                  budget: minValue * (grossMultiplier || 0),
                  aggregation: 'Low',
                },
              ],
              false,
              heading,
              shouldRenderFallback,
            )}
        </SimpleCard>
        {/* DetailedChartViewDialog */}
        {renderData && (
          <DetailedChartViewDialog title={heading} open={openChartDialog} onClose={handleDialogClose} groupedData={groupedData || []} />
        )}
      </>
    );
  },
);
CCReportCostCard.displayName = 'CCReportCostCard';

const renderCalculations = (calculations: Calculation[], isProjection: boolean, heading: string, showHeading: boolean) => {
  const avgCalculation = Math.round(parseFloat(calculations.find(c => c.aggregation === 'Average')?.budget.toFixed(2) || '0'));
  const highCalculation = Math.round(parseFloat(calculations.find(c => c.aggregation === 'High')?.budget.toFixed(2) || '0'));
  const lowCalculation = Math.round(parseFloat(calculations.find(c => c.aggregation === 'Low')?.budget.toFixed(2) || '0'));

  // Format numbers with commas
  const formattedAvgCalculation = formatNumberWithCommas(avgCalculation.toString());
  const formattedHighCalculation = formatNumberWithCommas(highCalculation.toString());
  const formattedLowCalculation = formatNumberWithCommas(lowCalculation.toString());

  // Check if all values are zero
  if (avgCalculation === 0 && highCalculation === 0 && lowCalculation === 0) {
    return (
      <>
        {showHeading && (
          <Box display='flex' alignItems='center' flexDirection='row' mt={1}>
            <Typography variant='h6' color='primary' sx={{ textTransform: 'uppercase' }}>
              {heading}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', my: '50px' }}>
          <Typography variant='h6' color='textSecondary' sx={{ fontWeight: '300' }}>
            No data available
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      {isProjection && <Divider flexItem sx={{ mb: 3 }} />}
      {showHeading && (
        <Box display='flex' alignItems='center' flexDirection='row' mt={1}>
          <Typography variant='h6' color='primary' sx={{ textTransform: 'uppercase' }} fontWeight={isProjection ? 500 : 600}>
            {heading}
          </Typography>
        </Box>
      )}
      <Box py={2}>
        <Box my={1} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Typography variant={isProjection ? 'h2' : 'h1'} borderBottom={'none'}>
            ${formattedAvgCalculation}
          </Typography>
        </Box>
        <Box my={1} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: 2 }}>
          <Typography
            variant={isProjection ? 'subtitle2' : 'subtitle1'}
            color='textSecondary'
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <ArrowUpward sx={{ mr: 1, mb: '2px', color: 'darkgreen', opacity: '0.5' }} />${formattedHighCalculation}
          </Typography>
          <Typography
            variant={isProjection ? 'subtitle2' : 'subtitle1'}
            color='textSecondary'
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            ${formattedLowCalculation}
            <ArrowDownward sx={{ ml: 1, mb: '2px', color: 'darkred', opacity: '0.5' }} />
          </Typography>
        </Box>
      </Box>
    </>
  );
};
export default CCReportCostCard;
